import { v4 as uuidv4 } from "uuid";
import { Candidate } from "@/types/candidate";
import { supabase } from "@/integrations/supabase/client";
import { extractTextFromFile } from "./cv-extractor";
import { sanitizeStorageFileName } from "@/utils/storage";
import { display } from "html2canvas/dist/types/css/property-descriptors/display";


export interface CandidateFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
    skills: string;
    summary: string;
    linkedin?: string;
    cvFile: File | null;
    linkedinJson?: string; // Added to store the full LinkedIn JSON data
    _initialLanguages?: Array<{ language: string; language_code: string; cefr_level: number }>;
}

export async function createCandidate(
    formData: CandidateFormData & { name: string, custom_fields?: Record<string, any> },
    organizationUuid: string,
    updateProgress?: (stage: string | null, progress: number) => void
): Promise<Candidate | null> {
    try {
        updateProgress && updateProgress("Creating candidate record", 10);



        // Extract skills array from comma-separated string
        // Handle empty string case to prevent malformed array literal error
        const skills = formData.skills && formData.skills.trim() !== ""
            ? formData.skills.split(",").map((skill) => skill.trim()).filter(skill => skill.length > 0)
            : [];

        // Create the candidate record
        const insertData = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            function: formData.role,
            skills: skills,
            status: "new",
            linkedin: formData.linkedin,
            linkedin_json: formData.linkedinJson ? JSON.parse(formData.linkedinJson) : null,
            ai_synthesis: formData.summary || null,
            organization_uuid: organizationUuid,
            ...(formData.custom_fields && Object.keys(formData.custom_fields).length > 0 ? { custom_fields: formData.custom_fields } : {})
        };

        const { data: candidateData, error: createError } = await supabase
            .from("candidates")
            .insert(insertData)
            .select()
            .single();

        if (createError) {
            console.error("Error creating candidate:", createError);
            throw new Error(createError.message);
        }

        updateProgress && updateProgress("Candidate record created", 40);

        const candidateId = candidateData.id;

        // If there's a CV file, process it
        if (formData.cvFile) {
            try {
                updateProgress && updateProgress("Uploading CV file", 50);

                // Extract text from the CV
                const textContent = await extractTextFromFile(formData.cvFile);

                updateProgress && updateProgress("Processing CV content", 70);

                // Generate a unique, storage-safe filename (Supabase rejects non-ASCII keys)
                const timestamp = Date.now();
                const safeName = sanitizeStorageFileName(formData.cvFile.name);
                const dotIndex = safeName.lastIndexOf('.');
                const baseName = dotIndex > 0 ? safeName.slice(0, dotIndex) : safeName;
                const fileExt = dotIndex > 0 ? safeName.slice(dotIndex + 1) : '';
                const fileName = `${baseName}_${timestamp}${fileExt ? `.${fileExt}` : ''}`;
                const filePath = `${candidateId}/cv/${fileName}`;

                //console.log("Uploading CV to path:", filePath);
                // Upload the file to Supabase Storage (using the legacy approach)
                const { error: uploadError, data: uploadData } = await supabase.storage
                    .from('cvs')
                    .upload(filePath, formData.cvFile, {
                        upsert: true
                    });



                // we also make an entry in the documents table
                if (!uploadError) {
                    await supabase.from("documents").insert({
                        id: uploadData.id,
                        name: fileName,
                        storage_id: uploadData.id,
                        text_extracted: sanitizeText(textContent),
                        organization_uuid: candidateData.organization_uuid,
                        entity_id: candidateId,
                        category: 'cv',
                        display_name: formData.cvFile.name,
                    });
                }

                if (uploadError) {
                    console.error("Error uploading CV:", uploadError);
                    throw new Error("Failed to upload CV file");
                }

                updateProgress && updateProgress("Analyzing CV content", 80);

                // Call the analyze-resume function with the text content
                const { data: analysisData, error: analysisError } = await supabase.functions.invoke(
                    'ai-processing?command=push-new-analysis',
                    {
                        body: {
                            entityId: candidateId,
                            entityType: "candidate",
                            analysisType: "analyse-resume",
                            actionOnComplete: "update-candidate",
                        }
                    }
                );

                if (analysisError) {
                    console.error("Error analyzing resume:", analysisError);
                }

                updateProgress && updateProgress("CV processing complete", 90);
            } catch (error) {
                console.error("Error processing CV:", error);
            }
        } else if (formData.summary) {
            // If there's a summary but no CV, still update the candidate record
            const { error: updateError } = await supabase
                .from("candidates")
                .update({
                    ai_synthesis: formData.summary
                })
                .eq("id", candidateId);

            if (updateError) {
                console.error("Error updating candidate with summary:", updateError);
            }
        }

        // NOTE: History tracking is now handled automatically by database triggers
        // No manual history logging needed here

        updateProgress && updateProgress("Candidate created successfully", 100);

        // Fetch the complete candidate record
        const { data: finalCandidateData, error: fetchError } = await supabase
            .from("candidates")
            .select("*")
            .eq("id", candidateId)
            .single();

        if (fetchError) {
            console.error("Error fetching final candidate data:", fetchError);
            return candidateData as Candidate;
        }

        return finalCandidateData as Candidate;
    } catch (error) {
        console.error("Error in createCandidate:", error);
        updateProgress && updateProgress(null, 0);
        return null;
    }
}

function sanitizeText(text: string): string {
    if (!text) return text;
    
    return text
        // Remove null bytes (the main culprit)
        .replace(/\0/g, '')
        // Remove other problematic control characters but keep newlines and tabs
        .replace(/[\x01-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
        // Clean up excessive whitespace
        .replace(/\s+/g, ' ')
        .trim();
}